// ===========================================================================
// Primitivos "composer" (FC7 cap.2 §H.3) — formulário seccionado de criação/
// importação: FormCard + seções com chip numerado + grid de 12 colunas +
// FormFoot.
//
// PROMOVIDOS de pages/Routing/RoutingItemNew/components/manual/styles.js em
// 2026-09-10, quando Transferências passou a reusá-los (migrate-on-touch do
// §H.4 — extrair, não copiar). O arquivo do Routing re-exporta daqui; este é
// o canônico. O que era específico do Routing (WindowBlock) ficou lá.
//
// Grid de 12 colunas: Field aceita $span 1..12 e colapsa para a largura total
// no mobile.
// ===========================================================================
import styled from 'styled-components';

// ---------------------------------------------------------------------------
// Card do formulário
// ---------------------------------------------------------------------------
export const FormCard = styled.div`
   max-width: 880px;
   margin: 0 auto;
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
   border: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.08)'};
   border-radius: 16px;
   box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.05),
      0 8px 30px rgba(0, 0, 0, 0.06);

   /* SEM overflow:hidden de propósito (2026-09-11). O recorte arredondava os
      cantos, mas também aprisionava qualquer popover absoluto dentro do card —
      o dropdown do ProductNameSearch, na seção de itens da transferência, era
      cortado na borda inferior. Mesma causa e mesma correção do card de
      filtros das cobranças (commit cc64c94): o arredondamento vai para o único
      filho com fundo próprio, o FormFoot. Os demais filhos herdam o branco do
      card, que já tem raio. */
`;

export const FormCardTop = styled.div`
   padding: 20px 24px;
   border-bottom: 1px solid ${({ theme }) => theme?.colors?.borderLight ?? 'rgba(0,0,0,0.06)'};

   h2 {
      font-size: 18px;
      font-weight: 700;
      letter-spacing: -0.01em;
      margin: 0 0 5px;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
   p {
      font-size: 13px;
      line-height: 1.5;
      margin: 0;
      max-width: 640px;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
`;

// ---------------------------------------------------------------------------
// Seção (chip-ícone numerado + título/subtítulo + slot à direita)
// ---------------------------------------------------------------------------
export const Section = styled.section`
   padding: 22px 24px;
   border-top: 1px solid ${({ theme }) => theme?.colors?.borderLight ?? 'rgba(0,0,0,0.06)'};

   &:first-of-type {
      border-top: none;
   }
`;

export const SectionHead = styled.div`
   display: flex;
   align-items: center;
   gap: 13px;
   margin-bottom: 18px;
`;

export const SectionIcon = styled.span`
   position: relative;
   flex-shrink: 0;
   width: 38px;
   height: 38px;
   border-radius: 11px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   background: ${({ $warm, theme }) =>
      ($warm
         ? 'rgba(232,132,60,0.12)'
         : theme?.colors?.primarySoft ?? 'rgba(24,95,165,0.12)')};
   color: ${({ $warm, theme }) =>
      ($warm ? '#c9772f' : theme?.colors?.primary ?? '#185FA5')};

   .step {
      position: absolute;
      top: -6px;
      right: -6px;
      width: 18px;
      height: 18px;
      border-radius: 999px;
      background: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 2px solid #fff;
   }
`;

export const SectionTitles = styled.div`
   min-width: 0;

   .t {
      font-size: 15px;
      font-weight: 700;
      letter-spacing: -0.01em;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
   .d {
      font-size: 12.5px;
      margin-top: 1px;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
`;

export const SectionHeadRight = styled.div`
   margin-left: auto;
`;

// ---------------------------------------------------------------------------
// Segmented control (alternância de modos dentro de uma seção)
// ---------------------------------------------------------------------------
export const ModeSegmented = styled.div`
   display: inline-flex;
   gap: 3px;
   padding: 4px;
   border-radius: 11px;
   background: ${({ theme }) => theme?.colors?.surfaceMuted ?? '#eef1f5'};

   button {
      appearance: none;
      border: 0;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 9px 15px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      background: transparent;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
      transition:
         background 0.13s ease,
         color 0.13s ease;

      &.active {
         background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
         color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
         box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
      }
      &.active svg {
         color: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
      }
      &:disabled {
         cursor: not-allowed;
         opacity: 0.5;
      }
   }
`;

// ---------------------------------------------------------------------------
// Grid 12 colunas + Field
// ---------------------------------------------------------------------------
export const FormGrid = styled.div`
   display: grid;
   /* minmax(0, 1fr), não 1fr: track "1fr" tem mínimo = min-content, e um
      select com option longa (nome de PDV) infla esse mínimo e faz o card
      ESTOURAR por baixo do vizinho quando o composer divide a largura com um
      aside (bug real na Nova Transferência, 2026-09-10). Com mínimo zero o
      campo encolhe e o texto trunca — que é o comportamento certo. */
   grid-template-columns: repeat(12, minmax(0, 1fr));
   gap: 12px;
   margin-bottom: ${({ $mb = 0 }) => `${$mb}px`};

   @media (max-width: 640px) {
      grid-template-columns: 1fr;
   }
`;

export const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   min-width: 0;
   grid-column: ${({ $span = 12 }) => `span ${$span}`};

   @media (max-width: 640px) {
      grid-column: 1 / -1;
   }
`;

export const FieldLabel = styled.label`
   font-size: 11.5px;
   font-weight: 600;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};

   .req {
      color: ${({ theme }) => theme?.colors?.danger ?? '#c62828'};
      margin-left: 2px;
   }
`;

const controlStyles = `
   height: 40px;
   padding: 0 11px;
   border-radius: 11px;
   font-size: 14px;
   font-family: inherit;
`;

export const FieldInput = styled.input`
   ${controlStyles}
   border: 1px solid ${({ $invalid, theme }) =>
      ($invalid
         ? theme?.colors?.danger ?? '#c62828'
         : '#dde2e9')};
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};

   &:focus {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
      box-shadow: 0 0 0 3px rgba(24, 95, 165, 0.16);
   }
   &:disabled {
      background: ${({ theme }) => theme?.colors?.surfaceMuted ?? 'rgba(0,0,0,0.04)'};
      cursor: not-allowed;
   }
`;

export const FieldSelect = styled.select`
   ${controlStyles}
   border: 1px solid ${({ $invalid, theme }) =>
      ($invalid
         ? theme?.colors?.danger ?? '#c62828'
         : '#dde2e9')};
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};

   &:focus {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
      box-shadow: 0 0 0 3px rgba(24, 95, 165, 0.16);
   }
   &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
   }
`;

export const FieldTextarea = styled.textarea`
   min-height: 72px;
   padding: 9px 11px;
   border-radius: 11px;
   font-size: 14px;
   font-family: inherit;
   resize: vertical;
   border: 1px solid #dde2e9;
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};

   &:focus {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
      box-shadow: 0 0 0 3px rgba(24, 95, 165, 0.16);
   }
`;

export const FieldError = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   font-size: 11.5px;
   font-weight: 600;
   color: ${({ theme }) => theme?.colors?.danger ?? '#c62828'};
`;

export const FieldHint = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   opacity: 0.9;
`;

// ---------------------------------------------------------------------------
// Normalizador de controles de terceiros (CurrencyInput, *NameSearch) — esses
// componentes renderizam <input> "pelado" que cai no estilo global (borda
// sólida do browser, cantos retos). Este wrapper alinha borda/raio/altura ao
// FieldInput SEM tocar nos componentes compartilhados. Preserva o
// padding-right deles (botão de limpar) e só reforça padding no CurrencyInput
// (.currency-field), que não tem autocomplete.
// ---------------------------------------------------------------------------
export const ControlScope = styled.div`
   input {
      height: 40px;
      border: 1px solid #dde2e9;
      border-radius: 11px;
      background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
      font-size: 14px;
      font-family: inherit;
   }
   input:focus {
      outline: none;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
      box-shadow: 0 0 0 3px rgba(24, 95, 165, 0.16);
   }
   .currency-field {
      width: 100%;
      padding: 0 11px;
   }
`;

export const InlineCheckboxLabel = styled.label`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   margin-top: 6px;
   font-size: 13px;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   cursor: pointer;
   user-select: none;
`;

// ---------------------------------------------------------------------------
// Detalhes opcionais (colapsável)
// ---------------------------------------------------------------------------
export const Collapse = styled.div`
   border-top: 1px solid ${({ theme }) => theme?.colors?.borderLight ?? 'rgba(0,0,0,0.06)'};
`;

export const CollapseHead = styled.button`
   display: flex;
   align-items: center;
   gap: 11px;
   width: 100%;
   padding: 18px 24px;
   background: transparent;
   border: 0;
   cursor: pointer;
   text-align: left;
   transition: background 0.12s ease;

   &:hover {
      background: rgba(24, 95, 165, 0.04);
   }

   .chev {
      display: inline-flex;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
      transition: transform 0.18s ease;
      transform: ${({ $open }) => ($open ? 'rotate(90deg)' : 'none')};
   }
   .ic {
      width: 38px;
      height: 38px;
      border-radius: 11px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: #f0f2f5;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
   .ttl {
      font-size: 15px;
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
   .meta {
      font-size: 12px;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
   .badge {
      margin-left: auto;
      padding: 3px 10px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.primary ?? '#185FA5'};
      background: ${({ theme }) => theme?.colors?.primarySoft ?? 'rgba(24,95,165,0.12)'};
   }
`;

export const CollapseBody = styled.div`
   padding: 4px 24px 24px;
`;

export const Subhead = styled.div`
   display: flex;
   align-items: center;
   gap: 9px;
   margin: ${({ $first }) => ($first ? '6px 0 4px' : '22px 0 4px')};

   .sic {
      display: inline-flex;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
   .st {
      font-size: 13.5px;
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
`;

export const Subdesc = styled.p`
   margin: 0 0 13px;
   font-size: 12px;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
`;

export const PermNote = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   margin-top: 6px;
   padding: 13px 15px;
   border: 1px dashed #d8dde4;
   border-radius: 11px;
   background: #fafbfc;
   font-size: 12.5px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};

   .ic {
      flex-shrink: 0;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
`;

// ---------------------------------------------------------------------------
// Layout de página do composer com ASIDE (promovido de TransferOrderNew em
// 2026-09-10, quando a edição do pedido virou o 2º consumidor): FormCard à
// esquerda + cartão lateral sticky (resumo/checklist/ações) à direita.
// ---------------------------------------------------------------------------

export const ComposerPageGrid = styled.div`
   display: grid;
   grid-template-columns: minmax(0, 1fr) minmax(280px, 320px);
   gap: 16px;
   align-items: start;

   /* Item de grid tem min-width:auto — sem isto, conteúdo não-encolhível
      dentro do FormCard estoura o track e desliza por baixo do aside,
      engolindo o gap. */
   > * {
      min-width: 0;
   }

   @media (max-width: 960px) {
      grid-template-columns: 1fr;
   }
`;

export const AsideCard = styled.aside`
   position: sticky;
   top: 16px;
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
   border: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.08)'};
   border-radius: 16px;
   padding: 18px 20px;
   box-shadow: ${({ theme }) => theme?.shadows?.sm ?? '0 1px 2px rgba(0,0,0,0.04)'};

   h2 {
      margin: 0 0 12px;
      font-size: 15px;
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
`;

export const AsideRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 12px;
   padding: 5px 0;
   font-size: 13px;

   .lbl {
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
   .val {
      font-weight: 700;
      text-align: right;
      overflow-wrap: anywhere;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
   }
`;

export const AsideChecklist = styled.ul`
   list-style: none;
   margin: 14px 0 16px;
   padding: 12px 0 0;
   border-top: 1px solid ${({ theme }) => theme?.colors?.borderLight ?? 'rgba(0,0,0,0.06)'};
   display: grid;
   gap: 8px;
`;

// $ok=true → linha verde informativa; $ok=false → aviso âmbar (nunca é erro
// bloqueante por si — quem bloqueia é a ação, desabilitada).
export const AsideCheckItem = styled.li`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   font-size: 12.5px;
   line-height: 1.45;
   font-weight: ${({ $ok }) => ($ok ? 400 : 700)};
   color: ${({ $ok }) => ($ok ? 'rgba(0,0,0,0.7)' : '#8a5a00')};

   .ic {
      flex-shrink: 0;
      margin-top: 1px;
      color: ${({ $ok }) => ($ok ? '#1b5e20' : '#d97706')};
   }
`;

export const AsideActions = styled.div`
   display: grid;
   gap: 8px;

   button {
      width: 100%;
   }
`;

// ---------------------------------------------------------------------------
// Rodapé do card (CTA)
// ---------------------------------------------------------------------------
export const FormFoot = styled.div`
   display: flex;
   align-items: center;
   gap: 14px;
   flex-wrap: wrap;
   padding: 18px 24px;
   border-top: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.08)'};
   background: #fbfcfd;
   /* Acompanha o raio do card: é o que substitui o overflow:hidden removido
      do FormCard. Único filho com fundo próprio, e sempre o último. */
   border-radius: 0 0 16px 16px;

   .note {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      font-size: 12px;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
   .acts {
      display: flex;
      gap: 10px;
      margin-left: auto;
   }
`;
